import { CheckOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Empty, Modal, Space, Table, Typography, message } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';

import ProductImage from '../../../components/productImage';
import InventoryStatusBadge from '../components/InventoryStatusBadge';

const { Text, Title } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Тооллогын төлвийн монгол нэр */
const STATUS_ENUM = {
  created: 'Хүсэлт үүссэн',
  confirmed: 'Хүсэлт баталгаажсан',
  partialCompleted: 'Хийгдэж байна',
  completed: 'Амжилттай дууссан',
  canceled: 'Цуцлагдсан',
  refused: 'Татгалзсан',
};

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Огноог уншигдахуйц болгоно */
const formatDate = (value) => (value ? moment(value).format('YYYY-MM-DD') : '-');

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Картын гарчиг: зүүн талд дүрс */
const CardTitle = ({ icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    {icon}
    {children}
  </span>
);

/** Барааны зураг + нэр + SKU */
const ProductCell = ({ image, title, code }) => (
  <Space size={12} align="center">
    <ProductImage
      src={image}
      alt={title ?? 'Барааны зураг'}
      size={IMAGE_SIZE}
      style={{ objectFit: 'cover', borderRadius: 4, boxShadow: IMAGE_SHADOW }}
    />
    <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
      <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: title }}>
        {val(title)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, maxWidth: TITLE_MAX_WIDTH }} ellipsis>
        {code}
      </Text>
    </Space>
  </Space>
);

const columns = [
  {
    key: 'index',
    title: '№',
    width: 48,
    align: 'center',
    render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
  },
  {
    key: 'product',
    title: 'Бараа',
    render: (_, record) => (
      <ProductCell
        image={record?.productImage}
        title={record?.productTitle}
        code={record?.productSku}
      />
    ),
  },
  {
    key: 'totalUnit',
    title: 'Өмнө нь байсан тоо/ширхэг',
    align: 'center',
    width: 200,
    render: (_, record) => val(record?.totalUnit),
  },
  {
    key: 'updatedUnit',
    title: 'Тоолсон тоо/ширхэг',
    align: 'center',
    width: 200,
    // Зөрүүтэй бол тоолсон тоог онцолж харуулна
    render: (_, record) =>
      record?.updatedUnit !== record?.totalUnit ? (
        <Text strong>{val(record?.updatedUnit)}</Text>
      ) : (
        val(record?.updatedUnit)
      ),
  },
];

/**
 * Тооллогын дэлгэрэнгүй — Хүргэлтийн захиалгын дэлгэрэнгүйтэй (orderDelivery/
 * ListWithDetail/Detail.js) ижил Card + Descriptions хэв маягтай.
 */
const Detail = ({ detail = {}, loading = false, refreshDetail }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  // Илгээж буй шийдвэр ('completed' | 'refused') — тухайн товч дээр loading харуулна
  const [changingStatus, setChangingStatus] = useState(null);

  const products = detail?.products ?? [];

  /** Хянах модалын шийдвэрийг илгээнэ (completed — баталгаажуулах, refused — татгалзах) */
  const changeStatus = async (orderStatus) => {
    setChangingStatus(orderStatus);
    try {
      const { data } = await axios.post('/api/v1.0/request/confirmCount', {
        _id: detail?._id,
        orderStatus,
      });
      if (data.statusCode === 200) {
        setModalVisible(false);
        if (orderStatus === 'completed') {
          message.success('Амжилттай баталгаажлаа.');
        } else {
          message.error('Баталгаажуулалтыг татгалзлаа.');
        }
        refreshDetail?.();
      }
    } catch (error) {
      console.error('confirmCount: ', error);
    } finally {
      setChangingStatus(null);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        size="middle"
        loading={loading}
        title={
          <Title level={5} style={{ margin: 0 }}>
            {val(detail?.code)}
          </Title>
        }
        // Хийгдэж байгаа тооллогыг л хянах боломжтой
        extra={
          detail?.orderStatus === 'partialCompleted' && (
            <Button icon={<CheckOutlined />} onClick={() => setModalVisible(true)}>
              Хянах
            </Button>
          )
        }
      >
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 4 }}
          items={[
            { key: 'code', label: 'Захиалгын дугаар', children: val(detail?.code) },
            {
              key: 'status',
              label: 'Төлөв',
              children: (
                <InventoryStatusBadge
                  status={detail?.orderStatus}
                  label={STATUS_ENUM[detail?.orderStatus]}
                />
              ),
            },
            { key: 'createdAt', label: 'Үүссэн огноо', children: formatDate(detail?.createdAt) },
            {
              // Тооллогын гол талбар — өмнө нь дэлгэрэнгүйд хаана ч харагддаггүй байв
              key: 'inventoryDate',
              label: 'Тооллого хийх огноо',
              children: formatDate(detail?.inventoryDate),
            },
            {
              /**
               * Тооллого дууссан огноо нь `operatedDate` — өмнө нь `updatedAt`-ыг
               * харуулдаг байсан тул дөнгөж үүсгэсэн («Хүсэлт үүссэн» төлөвтэй)
               * хүсэлтэд ч дууссан огноо бөглөгдсөн харагдаж, жагсаалттай
               * зөрдөг байв (жагсаалт нь `operatedDate`-ыг зөв харуулдаг).
               */
              key: 'operatedDate',
              label: 'Дууссан огноо',
              children: formatDate(detail?.operatedDate),
            },
          ]}
        />
      </Card>

      <Card
        title={<CardTitle icon={<AiOutlineInfoCircle />}>Ажилтны мэдээлэл</CardTitle>}
        size="middle"
        loading={loading}
      >
        <Descriptions
          {...descriptionProps}
          items={[
            { key: 'name', label: 'Ажилтны нэр', children: val(detail?.hubman?.firstName) },
            { key: 'phone', label: 'Ажилтны утас', children: val(detail?.hubman?.phoneNumber) },
          ]}
        />
      </Card>

      <Card
        title={<CardTitle icon={<BsBox2 />}>Тоолсон барааны жагсаалт</CardTitle>}
        size="middle"
        loading={loading}
        styles={{ body: { padding: products.length ? 0 : undefined } }}
      >
        <div style={{ padding: '10px' }}>
          {products.length ? (
            <Table
              columns={columns}
              dataSource={products}
              rowKey={(record, index) => record?.productSku ?? index}
              size="middle"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Өгөгдөл байхгүй байна."
              style={{ margin: 0 }}
            />
          )}
        </div>
      </Card>

      <Modal
        title="Тооллого хянах"
        open={isModalVisible}
        closable
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button
            key="refuse"
            danger
            loading={changingStatus === 'refused'}
            disabled={Boolean(changingStatus) && changingStatus !== 'refused'}
            onClick={() => changeStatus('refused')}
          >
            Татгалзах
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={changingStatus === 'completed'}
            disabled={Boolean(changingStatus) && changingStatus !== 'completed'}
            onClick={() => changeStatus('completed')}
          >
            Баталгаажуулах
          </Button>,
        ]}
      >
        Тооллогын тоо ширхэгийг баталгаажуулах уу?
      </Modal>
    </Space>
  );
};

export default Detail;
